import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PRODUCTS } from "@/lib/content";

export default function ProductsGrid({ withHeading = true, max }) {
  const items = max ? PRODUCTS.slice(0, max) : PRODUCTS;
  return (
    <section
      data-testid="products-grid"
      className="border-t border-[var(--ri-ink)] bg-[var(--ri-bg)]"
    >
      {withHeading && (
        <div className="ri-container pt-20 md:pt-28 pb-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
            <div className="lg:col-span-7">
              <div className="overline mb-4">
                <span className="dot mr-2" /> 03 — Products
              </div>
              <h2 className="font-display text-5xl md:text-6xl tracking-tighter leading-[0.95]">
                Hardware & systems<br />
                built for{" "}
                <span className="italic font-display-soft">classrooms.</span>
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[var(--ri-muted)] text-base leading-relaxed">
                From robotics kits to AI labs and IoT benches — every product ships with curriculum, teacher training and warranty.
              </p>
              <Link
                to="/products"
                data-testid="products-grid-cta"
                className="btn-link mt-5 inline-flex"
              >
                See full catalog →
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="ri-container pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--ri-ink)] border border-[var(--ri-ink)]">
          {items.map((p, i) => (
            <motion.article
              key={p.sku}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="bg-white p-6 md:p-10 group"
              data-testid={`product-card-${p.sku.toLowerCase()}`}
            >
              <div className="flex items-center justify-between mb-6">
                <span className="ri-tag">{p.category}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ri-muted)]">
                  SKU · {p.sku}
                </span>
              </div>

              <div className="aspect-[16/10] overflow-hidden border border-[var(--ri-ink)] mb-6">
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04] grayscale group-hover:grayscale-0"
                />
              </div>

              <h3 className="font-display text-3xl md:text-4xl leading-[1.05] tracking-tight">
                {p.title}
              </h3>
              <p className="mt-3 text-[var(--ri-muted)] text-sm leading-relaxed">
                {p.description}
              </p>

              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-6">
                {p.specs.map((s, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-[13px]">
                    <span className="dot mt-1.5" />
                    <span>{s}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {p.useCases.map((u) => (
                  <span key={u} className="ri-tag text-[10px]">
                    {u}
                  </span>
                ))}
              </div>

              <div className="mt-7 flex items-center gap-3">
                <Link
                  to="/contact"
                  className="btn-primary"
                  data-testid={`product-inquire-${p.sku.toLowerCase()}`}
                >
                  Request a quote <ArrowUpRight size={14} />
                </Link>
                <Link to="/products" className="btn-link">
                  Specs →
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
